/**
 * Phase F3 — Provider Profile Editor
 * -----------------------------------
 * Lets a provider edit the marketing-facing fields stored on the compliance profile.
 *
 * Backend stores these on the compliance_profile (provider-only fields). To update
 * them we re-call POST /api/compliance/submit/provider with the merged payload,
 * which is the platform's canonical update mechanism (also re-triggers review).
 *
 * Endpoints used:
 *   GET  /api/compliance/me
 *   POST /api/compliance/submit/provider     (full payload — backend treats as upsert)
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Save, Globe, Building2, ImageIcon } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_TYPES = [
  'BROKER', 'FINANCIAL_ADVISOR', 'WEALTH_MANAGER', 'DAY_TRADER',
  'INVESTMENT_MANAGER', 'UTILITY_PROVIDER', 'SERVICE_PROVIDER', 'OTHER',
];

const inputStyle = {
  width: '100%', padding: '10px 12px', background: theme.colors.bg.page,
  border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm,
  color: theme.colors.text.primary, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary };
const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 22, boxShadow: theme.colors.shadows.sm,
};

export default function ProviderProfileEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    profile_image_url: '', biography: '', company_name: '', website: '',
    service_description: '', provider_types: ['BROKER'],
    // Required by compliance.submit/provider — preserved as-is from profile:
    legal_name: '', country: 'US', state_region: '', tax_id_last4: '',
  });

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/compliance/me`, auth);
        const p = res.data?.profile || null;
        if (cancelled) return;
        setProfile(p);
        if (p) {
          setForm({
            profile_image_url: p.profile_image_url || '',
            biography: p.biography || '',
            company_name: p.company_name || '',
            website: p.website || '',
            service_description: p.service_description || '',
            provider_types: p.provider_types?.length ? p.provider_types : ['BROKER'],
            legal_name: p.legal_name || '',
            country: p.country || 'US',
            state_region: p.state_region || '',
            tax_id_last4: p.tax_id_last4 || '',
          });
        }
      } catch (e) {
        if (!cancelled) toast.error('Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  const toggleType = (t) => {
    setForm((f) => {
      const has = f.provider_types.includes(t);
      const next = has ? f.provider_types.filter((x) => x !== t) : [...f.provider_types, t];
      return { ...f, provider_types: next.length ? next : ['BROKER'] };
    });
  };

  const save = async () => {
    if (!form.legal_name) {
      toast.error('Legal name is required (set it in the Compliance Center first).');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
      await axios.post(`${API}/compliance/submit/provider`, payload, auth);
      toast.success('Profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 24, color: theme.colors.text.muted }}>Loading…</div>;
  }

  return (
    <div data-testid="provider-profile-editor-page" style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <User size={26} color={theme.colors.primary} />
          Provider Profile Editor
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Update your public marketplace profile. Changes are part of your compliance submission.
        </p>
      </div>

      {/* Image preview */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{
            width: 96, height: 96, borderRadius: '50%',
            background: theme.colors.bg.tertiary, overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${theme.colors.borders}`, flexShrink: 0,
          }}>
            {form.profile_image_url ? (
              <img data-testid="profile-image-preview" src={form.profile_image_url} alt="profile"
                onError={(e) => { e.target.style.display = 'none'; }}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <ImageIcon size={28} color={theme.colors.text.muted} />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <label style={labelStyle}>Profile Image URL</label>
            <input data-testid="profile-image-url" style={inputStyle} value={form.profile_image_url}
              onChange={(e) => setForm({ ...form, profile_image_url: e.target.value })}
              placeholder="https://…" />
          </div>
        </div>
      </div>

      {/* Company info */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{
          fontSize: 14, fontWeight: 700, color: theme.colors.text.primary,
          marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Building2 size={16} color={theme.colors.primary} /> Company
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div>
            <label style={labelStyle}>Company Name</label>
            <input data-testid="profile-company-name" style={inputStyle} value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Website</label>
            <div style={{ position: 'relative' }}>
              <Globe size={14} color={theme.colors.text.muted}
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input data-testid="profile-website" style={{ ...inputStyle, paddingLeft: 30 }} value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
            </div>
          </div>
        </div>
      </div>

      {/* Bio + services */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Biography</label>
          <textarea data-testid="profile-bio" style={{ ...inputStyle, minHeight: 90, fontFamily: 'inherit' }}
            value={form.biography} onChange={(e) => setForm({ ...form, biography: e.target.value })}
            placeholder="Tell creators about your background." />
        </div>
        <div>
          <label style={labelStyle}>Service Description</label>
          <textarea data-testid="profile-service-description" style={{ ...inputStyle, minHeight: 90, fontFamily: 'inherit' }}
            value={form.service_description} onChange={(e) => setForm({ ...form, service_description: e.target.value })}
            placeholder="What services do you offer? Pricing, scope, timelines." />
        </div>
      </div>

      {/* Provider classifications */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{
          fontSize: 14, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 12,
        }}>Provider Classifications</div>
        <div data-testid="profile-classifications-grid" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PROVIDER_TYPES.map((t) => {
            const active = form.provider_types.includes(t);
            return (
              <button
                key={t}
                data-testid={`profile-classification-${t}`}
                onClick={() => toggleType(t)}
                style={{
                  padding: '6px 14px', borderRadius: 999, cursor: 'pointer',
                  border: `1px solid ${active ? theme.colors.primary : theme.colors.borders}`,
                  background: active ? `${theme.colors.primary}15` : theme.colors.bg.card,
                  color: active ? theme.colors.primary : theme.colors.text.secondary,
                  fontSize: 12, fontWeight: 600,
                }}
              >
                {t.replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
      </div>

      <button
        data-testid="profile-save-btn"
        disabled={saving}
        onClick={save}
        style={{
          padding: '12px 22px', borderRadius: theme.radius.cta, border: 'none',
          background: theme.colors.primary, color: '#fff', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14,
        }}
      >
        <Save size={16} /> {saving ? 'Saving…' : 'Save Profile'}
      </button>
    </div>
  );
}
